import { NavigationItem } from "@/navigation/types";
import { AppStripItemConfig } from "./appStripTypes";

/**
 * Flatten navigation items recursively to get all items including nested children
 */
export function flattenNavigationItems(
  items: NavigationItem[],
): NavigationItem[] {
  const result: NavigationItem[] = [];
  items.forEach((item) => {
    result.push(item);
    if (item.children) {
      result.push(...flattenNavigationItems(item.children));
    }
  });
  return result;
}

/**
 * Check if a navigation item is visible based on permissions, roles, and CertainPath status
 */
function isNavItemVisible(
  item: NavigationItem,
  hasPermission: (p: string) => boolean,
  hasRole: (r: string) => boolean,
  hasCertainPathCompany: boolean,
): boolean {
  // Check CertainPath-only items
  let cpPass = true;
  if (item.isCertainPathOnly) {
    cpPass = hasCertainPathCompany;
  }

  // Check permissions (if specified)
  let permissionsPass = true;
  if (item.permissions && item.permissions.length > 0) {
    permissionsPass = item.permissions.some((p) => hasPermission(p));
  }

  // Check roles (if specified) — roles act as an alternative to permissions
  let rolesPass = true;
  if (item.roles && item.roles.length > 0) {
    rolesPass = item.roles.some((r) => hasRole(r));
  }

  // Item is visible if CertainPath check passes AND either permissions or roles pass
  const hasPermRestriction = item.permissions && item.permissions.length > 0;
  const hasRoleRestriction = item.roles && item.roles.length > 0;

  if (hasPermRestriction && hasRoleRestriction) {
    // If both are specified, either one passing is sufficient
    return cpPass && (permissionsPass || rolesPass);
  }

  return cpPass && permissionsPass && rolesPass;
}

/**
 * Check if an app strip item should be visible based on:
 * 1. Direct permissions (if specified)
 * 2. Direct roles (if specified)
 * 3. Whether any of its linked nav items are visible
 * 4. CertainPath company requirement
 */
export function isAppStripItemVisible(
  item: AppStripItemConfig,
  allNavItems: NavigationItem[],
  hasPermission: (p: string) => boolean,
  hasRole: (r: string) => boolean,
  hasCertainPathCompany: boolean,
): boolean {
  // Check CertainPath-only constraint first
  if (item.isCertainPathOnly && !hasCertainPathCompany) {
    return false;
  }

  // If item has direct permissions, check those
  if (item.permissions && item.permissions.length > 0) {
    const hasAnyPermission = item.permissions.some((p) => hasPermission(p));
    if (hasAnyPermission) {
      return true;
    }
    // If no direct permissions matched, fall through to check roles and navItems
  }

  // If item has roles, check those as a fallback
  if (item.roles && item.roles.length > 0) {
    const hasAnyRole = item.roles.some((r) => hasRole(r));
    if (hasAnyRole) {
      return true;
    }
  }

  // If item has navItemInternalNames, check if ANY of those nav items are visible
  if (item.navItemInternalNames && item.navItemInternalNames.length > 0) {
    const hasVisibleNavItem = item.navItemInternalNames.some((internalName) => {
      const navItem = allNavItems.find((n) => n.internalName === internalName);
      if (!navItem) return false;
      return isNavItemVisible(
        navItem,
        hasPermission,
        hasRole,
        hasCertainPathCompany,
      );
    });
    return hasVisibleNavItem;
  }

  // If had permissions or roles but none matched, and no navItemInternalNames, hide
  if (
    (item.permissions && item.permissions.length > 0) ||
    (item.roles && item.roles.length > 0)
  ) {
    return false;
  }

  // If no permissions, roles, or navItemInternalNames, item is visible by default
  return true;
}
